import { useEffect, useState } from "react";

import {
  Button,
  Card,
  CardHeader,
  ErrorState,
  SelectField,
  SkeletonRows,
  TextField,
  ToggleField,
} from "@/components/ui";
import { settingsRepository } from "@/repositories/settingsRepository";
import { useAuthStore } from "@/stores/authStore";

import {
  settingsKeys,
  useInvalidate,
  useMerchant,
  useMutation,
  useOperationalSettings,
} from "../useSettingsData";

/** Business profile and the operational rules that govern how the shop trades. */
export function BusinessTab() {
  const canManage = useAuthStore((s) => s.can("settings.manage"));
  // Platform staff belong to no merchant. Every endpoint on this screen is
  // merchant-scoped, so for them there is nothing to fetch and no amount of
  // retrying will change that.
  const hasMerchant = useAuthStore((s) => s.session?.merchant != null);

  const merchant = useMerchant();
  const settings = useOperationalSettings();

  if (!hasMerchant) {
    return (
      <Card flush>
        <ErrorState
          title="This account is not linked to a shop"
          description={
            "You are signed in as platform staff, who belong to no merchant. " +
            "Sign in with an account belonging to the shop to manage its settings."
          }
        />
      </Card>
    );
  }

  if (merchant.isLoading || settings.isLoading) return <SkeletonRows rows={6} />;
  if (merchant.isError || settings.isError) {
    return (
      <Card flush>
        <ErrorState
          description="Could not load your business settings."
          onRetry={() => {
            void merchant.refetch();
            void settings.refetch();
          }}
        />
      </Card>
    );
  }

  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <ProfileCard readOnly={!canManage} />
      <TradingRulesCard readOnly={!canManage} />
    </div>
  );
}

function ProfileCard({ readOnly }: { readOnly: boolean }) {
  const { data } = useMerchant();
  const invalidate = useInvalidate(settingsKeys.merchant);
  const [form, setForm] = useState(data);
  const [saved, setSaved] = useState(false);

  useEffect(() => setForm(data), [data]);

  const save = useMutation({
    mutationFn: settingsRepository.updateMerchant,
    onSuccess: () => {
      invalidate();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  if (!form) return null;

  return (
    <Card className="space-y-4">
      <CardHeader
        title="Business profile"
        subtitle="Printed on receipts and shown to customers."
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Registered name"
          value={form.name}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <TextField
          label="Trading name"
          hint="What the public knows you as, if different."
          value={form.trading_name}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, trading_name: e.target.value })}
        />
        <TextField
          label="Phone"
          value={form.phone}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <TextField
          label="WhatsApp"
          value={form.whatsapp}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
        />
        <TextField
          label="Registration number"
          value={form.registration_number}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
        />
        <TextField
          label="VAT number"
          hint="Only if you are registered for VAT."
          value={form.vat_number}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
        />
      </div>

      <div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
        <SelectField
          label="VAT"
          hint={
            form.vat_mode === "NONE"
              ? "Most spaza shops are below the R1 million threshold and charge no VAT."
              : form.vat_mode === "INCLUSIVE"
                ? "Shelf prices include VAT; it is calculated out for reporting."
                : "VAT is added on top of the marked price at the till."
          }
          value={form.vat_mode}
          disabled={readOnly}
          onChange={(e) =>
            setForm({ ...form, vat_mode: e.target.value as typeof form.vat_mode })
          }
          options={[
            { value: "NONE", label: "Not registered — no VAT" },
            { value: "INCLUSIVE", label: "Prices include VAT" },
            { value: "EXCLUSIVE", label: "VAT added at the till" },
          ]}
        />
        <TextField
          label="VAT rate (%)"
          value={form.vat_rate}
          disabled={readOnly || form.vat_mode === "NONE"}
          onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
        />
        <TextField
          label="Trading day starts at (hour)"
          type="number"
          min={0}
          max={23}
          hint="Set to 3 or 4 if you trade past midnight, so a Friday night's takings report on Friday."
          value={form.business_day_start_hour}
          disabled={readOnly}
          onChange={(e) =>
            setForm({ ...form, business_day_start_hour: Number(e.target.value) })
          }
        />
      </div>

      {!readOnly && (
        <div className="flex items-center gap-3 border-t border-line pt-4">
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => save.mutate(form)}
          >
            Save changes
          </Button>
          {saved && <span className="text-sm text-brand-700">Saved</span>}
          {save.isError && (
            <span className="text-sm text-danger-700" role="alert">
              Could not save. Please try again.
            </span>
          )}
        </div>
      )}
    </Card>
  );
}

function TradingRulesCard({ readOnly }: { readOnly: boolean }) {
  const { data } = useOperationalSettings();
  const invalidate = useInvalidate(settingsKeys.settings);
  const [form, setForm] = useState(data);
  const [saved, setSaved] = useState(false);

  useEffect(() => setForm(data), [data]);

  const save = useMutation({
    mutationFn: settingsRepository.updateSettings,
    onSuccess: () => {
      invalidate();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  if (!form) return null;

  return (
    <Card className="space-y-4">
      <CardHeader title="Trading rules" subtitle="How the till behaves day to day." />

      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label="When stock would go negative"
          hint={
            form.negative_stock_policy === "BLOCK"
              ? "Refusing a cash sale loses money and teaches cashiers to work around the till."
              : "The sale is recorded and the product appears on the negative stock report."
          }
          value={form.negative_stock_policy}
          disabled={readOnly}
          onChange={(e) =>
            setForm({
              ...form,
              negative_stock_policy: e.target.value as typeof form.negative_stock_policy,
            })
          }
          options={[
            { value: "ALLOW", label: "Allow the sale and flag it" },
            { value: "WARN", label: "Warn the cashier but allow it" },
            { value: "BLOCK", label: "Refuse the sale" },
          ]}
        />
        <TextField
          label="Discount limit without approval (%)"
          value={form.max_discount_percent_without_approval}
          disabled={readOnly}
          onChange={(e) =>
            setForm({ ...form, max_discount_percent_without_approval: e.target.value })
          }
        />
        <TextField
          label="Cash rounding (R)"
          hint="No 1c, 2c or 5c coins circulate. Set 0 to switch rounding off."
          value={form.cash_rounding_increment}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, cash_rounding_increment: e.target.value })}
        />
        <TextField
          label="Warn about expiry (days ahead)"
          type="number"
          min={0}
          value={form.expiry_warning_days}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, expiry_warning_days: Number(e.target.value) })}
        />
        <TextField
          label="Largest refund with no signal (R)"
          hint="Cash only, for sales from the same till. Set 0 to need signal for every refund."
          value={form.offline_refund_limit}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, offline_refund_limit: e.target.value })}
        />
      </div>

      <div className="divide-y divide-line border-t border-line">
        <ToggleField
          label="Refunds need manager approval"
          hint="A manager enters their staff code and PIN at the till to approve each refund."
          checked={form.require_approval_for_refunds}
          disabled={readOnly}
          onChange={(v) => setForm({ ...form, require_approval_for_refunds: v })}
        />
        <ToggleField
          label="Print a receipt automatically"
          checked={form.auto_print_receipt}
          disabled={readOnly}
          onChange={(v) => setForm({ ...form, auto_print_receipt: v })}
        />
        <ToggleField
          label="Low stock alerts"
          checked={form.low_stock_alerts_enabled}
          disabled={readOnly}
          onChange={(v) => setForm({ ...form, low_stock_alerts_enabled: v })}
        />
        <ToggleField
          label="Allow a shift to start while offline"
          hint="Leave on so the shop can trade after a power cut."
          checked={form.allow_offline_shift_open}
          disabled={readOnly}
          onChange={(v) => setForm({ ...form, allow_offline_shift_open: v })}
        />
      </div>

      {!readOnly && (
        <div className="flex items-center gap-3 border-t border-line pt-4">
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(form)}>
            Save changes
          </Button>
          {saved && <span className="text-sm text-brand-700">Saved</span>}
        </div>
      )}
    </Card>
  );
}
